import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, Input, Label, Button, Select } from '@headlessui/react';
import { TrashIcon, PlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import ConfirmModal from '../shared/ConfirmModal';
import {
  getDomainConfigById,
  createDomainConfig,
  updateDomainConfig,
  deleteDomainConfig,
  DomainConfigDto,
} from '../../services/domainConfigService';

const DEFAULT_COLORS = {
  primary: '#2563eb',
  secondary: '#3b82f6',
  accent: '#60a5fa',
  primaryHover: '#1d4ed8',
};

const DEFAULT_FONT = 'Graphik, sans-serif';

const inputClass = clsx(
  'block w-full rounded-lg border border-slate-300 bg-white py-2.5 px-4 text-slate-900',
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
  'placeholder:text-slate-400'
);

const AddDomainConfig: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Managed separately since formik doesn't handle string arrays well
  const [precheckRecipients, setPrecheckRecipients] = useState<string[]>([]);
  const [precheckPosRecipients, setPrecheckPosRecipients] = useState<string[]>([]);

  const formik = useFormik({
    initialValues: {
      hostname: '',
      brandName: '',
      pageTitle: '',
      favicon: '',
      logosLight: '',
      logosDark: '',
      colorPrimary: DEFAULT_COLORS.primary,
      colorSecondary: DEFAULT_COLORS.secondary,
      colorAccent: DEFAULT_COLORS.accent,
      colorPrimaryHover: DEFAULT_COLORS.primaryHover,
      fontFamily: DEFAULT_FONT,
      emailAccountSlug: '',
    },
    onSubmit: async (values) => {
      try {
        const payload: DomainConfigDto = {
          hostname: values.hostname,
          brandName: values.brandName,
          pageTitle: values.pageTitle,
          favicon: values.favicon,
          logos: { light: values.logosLight, dark: values.logosDark },
          colors: {
            primary: values.colorPrimary,
            secondary: values.colorSecondary,
            accent: values.colorAccent,
            primaryHover: values.colorPrimaryHover,
          },
          fontFamily: values.fontFamily,
          emailAccountSlug: values.emailAccountSlug,
          precheckRecipients: precheckRecipients.filter((e) => e.trim() !== ''),
          precheckPosRecipients: precheckPosRecipients.filter((e) => e.trim() !== ''),
        };

        if (isEditing && id) {
          await updateDomainConfig(parseInt(id), payload);
        } else {
          await createDomainConfig(payload);
        }
        setError('');
        navigate('/domain-configs');
      } catch {
        setError(t('domainConfig.errorSaving'));
      }
    },
  });

  useEffect(() => {
    if (id) {
      setIsEditing(true);
      getDomainConfigById(parseInt(id))
        .then((cfg) => {
          formik.setValues({
            hostname: cfg.hostname,
            brandName: cfg.brandName,
            pageTitle: cfg.pageTitle,
            favicon: cfg.favicon,
            logosLight: cfg.logos.light,
            logosDark: cfg.logos.dark,
            colorPrimary: cfg.colors.primary,
            colorSecondary: cfg.colors.secondary,
            colorAccent: cfg.colors.accent,
            colorPrimaryHover: cfg.colors.primaryHover,
            fontFamily: cfg.fontFamily,
            emailAccountSlug: cfg.emailAccountSlug,
          });
          setPrecheckRecipients(cfg.precheckRecipients || []);
          setPrecheckPosRecipients(cfg.precheckPosRecipients || []);
        })
        .catch(() => setError(t('domainConfig.errorLoadingSingle')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteDomainConfig(parseInt(id));
      navigate('/domain-configs');
    } catch {
      setError(t('domainConfig.errorDeleting'));
    }
    setShowDeleteModal(false);
  };

  const addRecipient = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setList([...list, '']);
  };

  const updateRecipient = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
    value: string,
  ) => {
    const updated = [...list];
    updated[index] = value;
    setList(updated);
  };

  const removeRecipient = (
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    index: number,
  ) => {
    setList(list.filter((_, i) => i !== index));
  };

  const renderRecipientList = (
    label: string,
    description: string,
    list: string[],
    setList: React.Dispatch<React.SetStateAction<string[]>>,
  ) => (
    <Field>
      <Label className="block text-sm font-medium text-slate-700 mb-1">{label}</Label>
      <p className="text-slate-500 text-sm mb-3">{description}</p>
      <div className="space-y-2">
        {list.map((email, i) => (
          <div key={i} className="flex gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRecipient(list, setList, i, e.target.value)}
              placeholder="email@example.com"
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => removeRecipient(list, setList, i)}
              className="px-2 text-red-500 hover:text-red-700 transition-colors"
            >
              <TrashIcon className="size-5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addRecipient(list, setList)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <PlusIcon className="size-4" />
          {t('domainConfig.addRecipient')}
        </button>
      </div>
    </Field>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          {isEditing ? t('domainConfig.editDomain') : t('domainConfig.addDomain')}
        </h1>
        <p className="text-slate-600 mt-1">
          {isEditing ? t('domainConfig.editSubtitle') : t('domainConfig.addSubtitle')}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        {/* General Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <h2 className="text-lg font-semibold text-slate-900 border-b border-slate-100 pb-3">{t('domainConfig.general')}</h2>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.hostname')}</Label>
            <Input
              id="hostname"
              type="text"
              value={formik.values.hostname}
              onChange={formik.handleChange}
              required
              placeholder={t('domainConfig.hostnamePlaceholder')}
              className={inputClass}
            />
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600">
              <InformationCircleIcon className="size-4 shrink-0" />
              {t('domainConfig.hostnameHint')}
            </p>
          </Field>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.brandName')}</Label>
            <Input
              id="brandName"
              type="text"
              value={formik.values.brandName}
              onChange={formik.handleChange}
              required
              placeholder={t('domainConfig.brandNamePlaceholder')}
              className={inputClass}
            />
          </Field>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.pageTitle')}</Label>
            <Input
              id="pageTitle"
              type="text"
              value={formik.values.pageTitle}
              onChange={formik.handleChange}
              required
              placeholder={t('domainConfig.pageTitlePlaceholder')}
              className={inputClass}
            />
          </Field>
        </div>

        {/* Logos & Favicon */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <h2 className="text-lg font-semibold text-slate-900 border-b border-slate-100 pb-3">{t('domainConfig.logosFavicon')}</h2>
          <p className="flex items-center gap-1.5 text-xs text-amber-600">
            <InformationCircleIcon className="size-4 shrink-0" />
            {t('domainConfig.logosHint')}
          </p>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.favicon')}</Label>
            <Input
              id="favicon"
              type="text"
              value={formik.values.favicon}
              onChange={formik.handleChange}
              required
              placeholder={t('domainConfig.faviconPlaceholder')}
              className={inputClass}
            />
          </Field>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.logoLight')}</Label>
            <Input
              id="logosLight"
              type="text"
              value={formik.values.logosLight}
              onChange={formik.handleChange}
              required
              placeholder={t('domainConfig.logoLightPlaceholder')}
              className={inputClass}
            />
          </Field>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.logoDark')}</Label>
            <Input
              id="logosDark"
              type="text"
              value={formik.values.logosDark}
              onChange={formik.handleChange}
              required
              placeholder={t('domainConfig.logoDarkPlaceholder')}
              className={inputClass}
            />
          </Field>

          {/* Preview */}
          {(formik.values.logosLight || formik.values.logosDark) && (
            <div className="flex gap-6 pt-2">
              {formik.values.logosLight && (
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-xs text-slate-400 mb-2">{t('domainConfig.logoLightPreview')}</p>
                  <img src={formik.values.logosLight} alt={t('domainConfig.logoLightPreview')} className="h-10 w-auto" />
                </div>
              )}
              {formik.values.logosDark && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <p className="text-xs text-slate-500 mb-2">{t('domainConfig.logoDarkPreview')}</p>
                  <img src={formik.values.logosDark} alt={t('domainConfig.logoDarkPreview')} className="h-10 w-auto" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Colors & Font */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <h2 className="text-lg font-semibold text-slate-900 border-b border-slate-100 pb-3">{t('domainConfig.colorsFont')}</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { id: 'colorPrimary', label: t('domainConfig.colorPrimary') },
              { id: 'colorSecondary', label: t('domainConfig.colorSecondary') },
              { id: 'colorAccent', label: t('domainConfig.colorAccent') },
              { id: 'colorPrimaryHover', label: t('domainConfig.colorPrimaryHover') },
            ].map(({ id, label }) => (
              <Field key={id}>
                <Label className="block text-sm font-medium text-slate-700 mb-2">{label}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    id={id}
                    value={(formik.values as any)[id]}
                    onChange={formik.handleChange}
                    className="h-10 w-10 rounded-lg border border-slate-300 cursor-pointer p-0.5"
                  />
                  <Input
                    type="text"
                    value={(formik.values as any)[id]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => formik.setFieldValue(id, e.target.value)}
                    className={clsx(inputClass, 'font-mono text-sm')}
                  />
                </div>
              </Field>
            ))}
          </div>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.fontFamily')}</Label>
            <Input
              id="fontFamily"
              type="text"
              value={formik.values.fontFamily}
              onChange={formik.handleChange}
              required
              placeholder="Graphik, sans-serif"
              className={inputClass}
            />
          </Field>
        </div>

        {/* Email Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <h2 className="text-lg font-semibold text-slate-900 border-b border-slate-100 pb-3">{t('domainConfig.emailSettings')}</h2>

          <Field>
            <Label className="block text-sm font-medium text-slate-700 mb-2">{t('domainConfig.emailAccountSlug')}</Label>
            <p className="text-slate-500 text-sm mb-2">{t('domainConfig.emailAccountSlugDescription')}</p>
            <Select
              id="emailAccountSlug"
              value={formik.values.emailAccountSlug}
              onChange={formik.handleChange}
              required
              className={clsx(inputClass, 'font-mono')}
            >
              <option value="" disabled>—</option>
              <option value="ccs">ccs</option>
              <option value="pp">pp</option>
              <option value="nxt">nxt</option>
            </Select>
          </Field>

          {renderRecipientList(
            t('domainConfig.precheckRecipients'),
            t('domainConfig.precheckRecipientsDescription'),
            precheckRecipients,
            setPrecheckRecipients,
          )}

          {renderRecipientList(
            t('domainConfig.precheckPosRecipients'),
            t('domainConfig.precheckPosRecipientsDescription'),
            precheckPosRecipients,
            setPrecheckPosRecipients,
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            type="submit"
            className="inline-flex items-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {isEditing ? t('domainConfig.editDomain') : t('domainConfig.addDomain')}
          </Button>

          {isEditing && (
            <Button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="inline-flex items-center px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              {t('common.delete')}
            </Button>
          )}

          <Button
            type="button"
            onClick={() => navigate('/domain-configs')}
            className="inline-flex items-center px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>

      {showDeleteModal && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title={t('domainConfig.deleteDomain')}
          message={t('domainConfig.deleteDomainMessage')}
        />
      )}
    </div>
  );
};

export default AddDomainConfig;
